import _, { isNil } from 'lodash';
import Immutable from 'seamless-immutable';
import { enums } from 'console-kit';

import RefData from 'ref-data';
import { displayContainerConstants, embedDestinationsModes } from '../../common/enums';

/**
 * Display Container
 * @typedef {object} DisplayContainer
 *  @property {string} name - the container name
 * @property {string} channelId - id of the channel associated with this display container
 * @property {string} channelRef - ref to the channel associated with this display container.
 *                                 prefer channelId
 *                                 format: "channel":[accountId]:[channelId]
 * @property {string} containerId
 * @property {number} createdBy
 * @property {number} displayUnitId
 * @property {string} embedUrl
 * @property {string} script the embed code
 * @property {string} scriptTemplate the embed code template
 * @property {boolean} hasDimensions - does the embed have dimensions defined.
 *  (Note: all display containers should have dimensions defined except when editing)
 * @property {string} iframe the advanced embed code
 * @property {string} iframeTemplate the advanced embed code template
 * @property {string} id
 * @property {boolean} isRemoving - marks the display container for removal. Useful if you want to do some sort of animation on the display container when removing it. If you immediately delete the item you can't animate it
 * @property {string} lastUpdated
 * @property {string} modifiedBy
 * @property {string} smartUrlId
 * @property {string} status
 * @property {string} sticky
 * @property {string} typeRef
 * @property {string} xDimension - the x dimension for the display container. Value will either be the width or the first part of the ratio, depending on whether it is responsive
 * @property {string} yDimension - the x dimension for the display container. Value will either be the height or the second part of the ratio, depending on whether it is responsive
 * @property {string} isRoot - a display container with this set to true will be the main container for custom domains to display experiences
 * @property {string} privacyMgmt - defines whether cookies stategy is handled on experience or customer side
 * @property {string} displayConfig - contains config for Lightbox(lightboxConfig) and Web Embed(webEmbed)
 * @property {object} privacyConfig - defines privacy config
 * @property {string} smartUrlPath - smart url of the channel
 */

const _keys = [
  'channelRef',
  'containerId',
  'containerRef',
  'createdBy',
  'displayUnit',
  'embedUrl',
  'growable',
  'height',
  'id',
  'iframe',
  'iframeTemplate',
  'invalidated',
  'isRoot',
  'lastUpdated',
  'modifiedBy',
  'name',
  'persistent',
  'postScript',
  'preScript',
  'ratio',
  'script',
  'scriptTemplate',
  'smartUrl',
  'status',
  'sticky',
  'typeRef',
  'width',
  'privacyMgmt',
  'displayConfig',
  'privacyConfig',
  'smartUrlPath',
];

function create(displayContainer) {
  const result = _.clone(displayContainer);

  result.displayUnitId = displayContainer.displayUnit;
  delete result.displayUnit;

  result.smartUrlId = displayContainer.smartUrl;
  delete result.smartUrl;

  result.typeRef = typeCheckRefData(displayContainer.typeRef);
  result._status = typeCheckRefData(displayContainer.status);
  delete result.status;

  result.xDimension = !isNil(displayContainer.width)
    ? displayContainer.width
    : displayContainer.growable && !!displayContainer.ratio && !isNaN(displayContainer.ratio.split(':')[0])
      ? displayContainer.ratio.split(':')[0]
      : null;

  result.yDimension = !isNil(displayContainer.height)
    ? displayContainer.height
    : displayContainer.growable && !!displayContainer.ratio && !isNaN(displayContainer.ratio.split(':')[1])
      ? displayContainer.ratio.split(':')[1]
      : null;

  result.preScript = displayContainer.preScript || '';
  result.postScript = displayContainer.postScript || '';
  result.privacyConfig = result.privacyConfig || { provider: '' }; //privacy config is only getting introduced, so could be null on the DB

  delete result.height;
  delete result.width;
  delete result.ratio;

  result.isRemoving = false;

  // mapping for the outdated namings on the backend side (since the fields in the DB won't be changed).
  // Field `isRoot` represents the default containers, `smartUrlPath === '/'` - root containers
  // but still keep a backend isDefault value in legacyIsDefault (not updating in UI)
  result.legacyIsDefault = result.isDefault;

  result.isDefault = result.isRoot;
  result.isRoot = result.smartUrlPath && result.smartUrlPath === '/';

  return Immutable(result, { prototype: model });
}

/**
 * Transforms a backend DisplayContainer object into a frontend DisplayContainer Model
 * @param {Object} displayContianer - backend formatted display container
 * @returns {DisplayContainer}
 */
function deserialize(displayContainer) {
  const { DEFAULT_LIGHTBOX_TRANSPARENCY } = displayContainerConstants;

  const result = _.pick(displayContainer, _keys);

  result.displayConfig = result.displayConfig ? JSON.parse(result.displayConfig) : { lightboxConfig: {}, webEmbed: {} };

  if (result.typeRef === RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.OVERLAY) {
    if (result.growable) {
      const [_widthRatio, _heightRatio] = _.get(displayContainer, 'ratio', 'undefined:undefined')
        .split(':')
        .map((ratio) => parseInt(ratio, 10));

      result.width = result.width || _widthRatio;
      result.height = result.height || _heightRatio;
    }

    if (_.isNil(result.displayConfig.lightboxConfig.transparency)) {
      result.displayConfig.lightboxConfig.transparency = DEFAULT_LIGHTBOX_TRANSPARENCY;
    }
  }

  result.privacyConfig = JSON.parse(result.privacyConfig);

  return create(result);
}

function typeCheckRefData(datum) {
  if (typeof datum === 'object' && datum.hasOwnProperty('id')) return RefData.Model.hydrate(datum.id);
  if (typeof datum === 'string') return RefData.Model.hydrate(datum);
}

/**
 * Transforms a frontend DisplayContainer Model into a backend DisplayContainer object
 * @param {Model} DisplayContainer - a DisplayContainer Model
 * @returns {displayContainer}
 */
function serialize(displayContainer, isMacroExperience) {
  const result = displayContainer.asMutable({ deep: true });

  result.displayUnit = displayContainer.displayUnitId;
  delete result.displayUnitId;

  result.smartUrl = displayContainer.smartUrlId;
  delete result.smartUrlId;

  result.ratio =
    !isMacroExperience && displayContainer.growable && displayContainer.xDimension && displayContainer.yDimension
      ? `${displayContainer.xDimension}:${displayContainer.yDimension}`
      : null;

  result.width = isMacroExperience || !displayContainer.growable ? displayContainer.xDimension : null;
  result.height = isMacroExperience || !displayContainer.growable ? displayContainer.yDimension : null;
  delete result.xDimension;
  delete result.yDimension;

  result.typeRef = displayContainer.typeRef.id;

  if (!!result.privacyConfig && !!result.privacyConfig.provider) {
    result.privacyConfig = JSON.stringify(result.privacyConfig);
  } else {
    delete result.privacyConfig;
  }

  delete result.isRemoving;

  result.displayConfig = JSON.stringify(result.displayConfig);

  // restoring the legacy backend fields
  result.isRoot = result.isDefault;
  result.isDefault = result.legacyIsDefault;

  return _.pick(result, _keys);
}

const model = {
  get channelId() {
    return _.parseInt(_.last(_.split(this.channelRef, ':')));
  },
  get campaignId() {
    return _.parseInt(_.last(_.split(this.containerRef, ':')));
  },
  // This should only return falsey while editing a publishing destination.
  // All persisted display containers should have dimensions
  get hasDimensions() {
    return !!(this.xDimension && this.yDimension);
  },
  get isPublished() {
    return this.status.id === RefData.Enum.STATUS.DISPLAYCONTAINER.PUBLISHED;
  },
  get isExpired() {
    return this.status.id === RefData.Enum.STATUS.DISPLAYCONTAINER.EXPIRED;
  },
  get isResponsive() {
    return this.growable;
  },
  get statusLabel() {
    return this._status.name;
  },
  get status() {
    return this._status;
  },
  set status(statusId) {
    this._status = RefData.Model.hydrate(statusId);
    return this;
  },

  get iconTypeRef() {
    if (this.isPersistent) return enums.icons.PIN;
    switch (this.typeRef.id) {
      case RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.EMBED:
      case RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.MICROSITE:
      case RefData.Enum.TYPE.DISPLAYCONTAINER_FB.CANVAS:
        return enums.icons.CODE;
      default:
        return enums.icons.SETTINGS;
    }
  },

  get containerType() {
    return _.head(this.containerRef.split(/:/g));
  },

  get isChannelContainer() {
    return this.containerType === 'channel';
  },

  get isCampaignContainer() {
    return this.containerType === 'campaign';
  },

  get embedType() {
    return this.growable ? embedDestinationsModes.RESPONSIVE : embedDestinationsModes.FIXED;
  },

  get embedSize() {
    return this.yDimension && this.xDimension ? `${this.yDimension}px x ${this.xDimension}px` : 'Automatic';
  },

  get typeRefLabel() {
    return this.typeRef.name;
  },

  get isWebMicrosite() {
    return this.typeRef.id === RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.MICROSITE;
  },

  get supportsAdvancedEmbed() {
    return this.isWebMicrosite || this.isWebEmbed;
  },

  get supportsEmbedCode() {
    return this.isWebEmbed;
  },

  get isWebEmbed() {
    return this.typeRef.id === RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.EMBED;
  },

  get isLightbox() {
    return this.typeRef.id === RefData.Enum.TYPE.DISPLAYCONTAINER_WEB.OVERLAY;
  },

  get isMultiple() {
    return this.typeRef.id === RefData.Enum.TYPE.DISPLAYCONTAINER.MULTIPLE;
  },

  get isPersistent() {
    return !!this.persistent;
  },
};

export default {
  create,
  deserialize,
  serialize,
};
